import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import Pagination10 from './components/Pagination10';
import PageFooter from './components/PageFooter';
import { formatString, truncateText } from './utils/StringUtils';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import usePagination10 from './components/hooks/usePagination10';
import Layout from './components/Layout';
import LoadingModal from './components/modals/LoadingModal';
import LoteModal from './components/modalServices/LoteModal';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import useProgressModal from './components/hooks/useProgressModal';
import ExcelUploadLoteModal from './components/modalServices/ExcelUploadLoteModal';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';

function DfdLotes() {
  const [showLoteModal, setShowLoteModal] = useState(false);
  const [loteParaEditar, setLoteParaEditar] = useState(null);
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);

  async function handleSaveLote(payload) {
    setLoading(true);
    try {
      let response;
      if (payload.modo === 'editar' && payload.id) {
        response = await axios.post(`${API_BASE_URL.DFD_LOTE}/save`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const loteEditado = response.data;
        setDfds(prev =>
          prev.map(l => (l.id === loteEditado.id ? loteEditado : l))
        );
        showDefaultModal('success', 'Lote editado com sucesso!');
      } else {
        response = await axios.post(`${API_BASE_URL.DFD_LOTE}/save`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const novoLote = response.data;
        setDfds(prev => [...prev, novoLote]);
        setSelectedDfd(novoLote);
        showDefaultModal('success', 'Lote cadastrado com sucesso!');
      }
    } catch (error) {
      showDefaultModal(
        'error',
        payload.modo === 'editar'
          ? 'Erro ao editar lote.'
          : 'Erro ao cadastrar lote.'
      );
    } finally {
      setLoading(false);
      setLoteParaEditar(null);
      setShowLoteModal(false);
    }
  }
  const [dfds, setDfds] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [shouldFetch, setShouldFetch] = useState(false);
  const location = useLocation();
  const dfdPrincipal = location.state?.dfd || null;
  const [selectedDfd, setSelectedDfd] = useState(dfdPrincipal);
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase = localStorage.getItem('anoBase');
  const navigate = useNavigate();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const {
    progressState,
    showProgressModal,
    updateProgress,
    hideProgressModal,
  } = useProgressModal();
  const totalItems = dfds.length;
  const { setPagination } = usePagination10(totalItems);

  const selectedDfds =
    dfds && setPagination
      ? dfds.slice(
          setPagination.startIndex,
          setPagination.startIndex + setPagination.rowsPerPage
        )
      : [];
  // Limpar seleção quando a lista paginada mudar e o selecionado não estiver visível
  useEffect(() => {
    if (selectedDfd && !selectedDfds.find(dfd => dfd.id === selectedDfd.id)) {
      setSelectedDfd(null);
    }
  }, [selectedDfds, selectedDfd]);

  // Função para gerenciar seleção por radio
  const handleRadioSelect = (dfd, event) => {
    event.stopPropagation();
    setSelectedDfd(dfd);
  };

  const handleRowClick = dfd => {
    // Se clicar no mesmo item já selecionado, desmarca
    if (selectedDfd && selectedDfd.id === dfd.id) {
      setSelectedDfd(null);
    } else {
      setSelectedDfd(dfd);
    }
  };

  // Funcionalidade de teclado simplificada
  useEffect(() => {
    const handleKeyDown = event => {
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.tagName === 'SELECT'
      ) {
        return; // Não interceptar se estiver em um campo de entrada
      }

      switch (event.key) {
        case 'Escape':
          setSelectedDfd(null);
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (selectedDfd) {
            const currentIndex = selectedDfds.findIndex(
              dfd => dfd.num === selectedDfd.num
            );
            if (currentIndex < selectedDfds.length - 1) {
              setSelectedDfd(selectedDfds[currentIndex + 1]);
            }
          } else if (selectedDfds.length > 0) {
            setSelectedDfd(selectedDfds[0]);
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (selectedDfd) {
            const currentIndex = selectedDfds.findIndex(
              dfd => dfd.num === selectedDfd.num
            );
            if (currentIndex > 0) {
              setSelectedDfd(selectedDfds[currentIndex - 1]);
            }
          } else if (selectedDfds.length > 0) {
            setSelectedDfd(selectedDfds[selectedDfds.length - 1]);
          }
          break;
        case ' ':
          event.preventDefault();
          if (selectedDfd) {
            // Para radio, espaço apenas confirma a seleção atual
            event.preventDefault();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedDfd, selectedDfds]);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL.DFD_LOTE}/all`, {
        params: {
          cnpj: cnpj,
          dfdNum: dfdPrincipal?.num,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setDfds(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter lotes:', error);
        navigate('/');
      });
    setLoading(false);
  }, [dfdPrincipal, cnpj, token, navigate, shouldFetch]);

  if (!dfds) {
    return <div>Loading...</div>;
  }

  function handleInformation(dfdToAprove) {
    axios
      .get(`${API_BASE_URL.TRAMITACAO}/justificativaDevolucao`, {
        params: {
          cnpjEntidade: dfdToAprove.cnpj,
          dfdNum: dfdToAprove.num,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        if (result.data) {
          const justificativa = result.data.justificativa;
          showDefaultModal(
            'information',
            `**Justificativa da devolução:**\n ${justificativa}`
          );
        } else {
        }
      })
      .catch(error => {
        console.error('Erro ao obter justificativa:', error);
        showDefaultModal('error', 'Erro ao obter justificativa!');
      });
  }
  function handleSend(dfdToAprove) {
    showConfirmModal('sendAprovacao', () => handleSendConfirm(dfdToAprove));
  }

  function handleSendConfirm(dfdToAprove) {
    if (dfdToAprove) {
      axios
        .get(`${API_BASE_URL.DFD_ITEM}/validate`, {
          params: {
            cnpj: dfdToAprove.cnpj,
            num: dfdToAprove.num,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          if (result.data) {
            if (
              !dfdToAprove.dataCriacao ||
              !dfdToAprove.dataContratacao ||
              !dfdToAprove.tipo ||
              !dfdToAprove.requisitante ||
              !dfdToAprove.objeto ||
              !dfdToAprove.tipoDemanda ||
              !dfdToAprove.regulamentacao
            ) {
              const missingFields = [];
              if (!dfdToAprove.dataCriacao)
                missingFields.push('Data de Criação');
              if (!dfdToAprove.dataContratacao)
                missingFields.push('Data da Contratação');
              if (!dfdToAprove.tipo) missingFields.push('Tipo');
              if (!dfdToAprove.requisitante) missingFields.push('Requisitante');
              if (!dfdToAprove.objeto) missingFields.push('Objeto');
              if (!dfdToAprove.tipoDemanda)
                missingFields.push('Tipo de Demanda');
              if (!dfdToAprove.regulamentacao)
                missingFields.push('Regulamentação Municipal');

              const plural = missingFields.length > 1 ? 'os campos' : 'o campo';
              const fields = missingFields.join(', ');

              showDefaultModal(
                'error',
                `Por favor, preencha ${plural}: ${fields}.`
              );
              return;
            }
            const updatedFormData = {
              ...dfdToAprove,
              tramitacao: 'ENVIADO PARA APROVAÇÃO',
            };
            axios
              .post(`${API_BASE_URL.DFD}/updateTramitacao`, updatedFormData, {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              })
              .then(result => {
                setDfds(prevState =>
                  prevState.map(item =>
                    item.num === dfdToAprove.num
                      ? { ...item, tramitacao: 'ENVIADO PARA APROVAÇÃO' }
                      : item
                  )
                );
                showDefaultModal(
                  'success',
                  'O registro foi enviado com sucesso'
                );
              })
              .catch(error => {
                console.error(
                  'Erro ao enviar o registro para aprovação:',
                  error
                );
                showDefaultModal(
                  'error',
                  'Erro ao enviar o registro para aprovação!'
                );
              });
          } else {
            showDefaultModal(
              'error',
              'Não é possível enviar para aprovação. Existem itens que precisam de atenção como categoria, unidade, quantidade e valor unitário!'
            );
          }
        })
        .catch(error => {
          console.error(
            'Não é possível enviar para aprovação. Existem itens que precisam de atenção como categoria, unidade, quantidade e valor unitário:',
            error
          );
          showDefaultModal(
            'error',
            'Não é possível enviar para aprovação. Existem itens que precisam de atenção como categoria, unidade, quantidade e valor unitário!'
          );
        });
    }
  }

  function handleDelete(dfdToDelete) {
    showConfirmModal('delete', () => handleDeleteConfirm(dfdToDelete));
  }

  function handleDeleteConfirm(dfdToDelete) {
    console.log('DADOS PARA DELETAR: ', dfdToDelete);
    if (dfdToDelete) {
      axios
        .get(`${API_BASE_URL.DFD_ITEM}/exists_lote`, {
          params: {
            cnpj: dfdToDelete.cnpj,
            dfdNum: dfdToDelete.dfdNum,
            loteNum: dfdToDelete.id,
          },
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(result => {
          if (result.data.exists) {
            showDefaultModal(
              'error',
              'Este registro possui vínculos e não pode ser excluído. Exclua os vínculos e tente novamente.'
            );
          } else {
            axios
              .delete(`${API_BASE_URL.DFD_LOTE}/delete`, {
                params: {
                  cnpj: dfdToDelete.cnpj,
                  dfdNum: dfdToDelete.dfdNum,
                  loteNum: dfdToDelete.id,
                },
                headers: { Authorization: `Bearer ${token}` },
              })
              .then(result => {
                setDfds(prevState =>
                  prevState.filter(item => item.num !== dfdToDelete.num)
                );
                showDefaultModal(
                  'success',
                  'O registro foi excluído com sucesso'
                );
              })
              .catch(error => {
                console.error('Erro ao excluir o item:', error);
                showDefaultModal('error', 'Erro ao excluir o registro!');
              });
          }
        });
    }
  }

  async function handleExportToExcel() {
    try {
      const response = await axios.post(
        `${API_BASE_URL.DFD}/exportExcelLote`,
        dfdPrincipal,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `DFD ${formatString(dfdPrincipal.dfdNum, 4)} LOTES.xlsx`;
      link.click();
    } catch (error) {
      console.error('Erro ao baixar o arquivo:', error);
    }
  }

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader
          title={`Lotes Cadastrados - DFD Nº ${dfdPrincipal ? formatString(dfdPrincipal.num, 4) : '--'}`}
          icon="bi-file-earmark-text"
          backButton={false}
          closeRoute="/dfds"
          closeState={{ dfd: dfdPrincipal }}
        />
        <PageContent>
          <Toolbar
            selectedId={selectedDfd}
            actions={[
              { key: 'novo', label: 'Novo Lote', icon: 'bi-plus-circle', variant: 'success', onClick: () => { setLoteParaEditar(null); setShowLoteModal(true); } },
              { key: 'editar', label: 'Editar', icon: 'bi-pencil-square', variant: 'primary', onClick: (lote) => { if (lote) { setLoteParaEditar(lote); setShowLoteModal(true); } }, requiresSelection: true },
              { key: 'justificativa', label: 'Justificativa', icon: 'bi-info-circle', variant: 'warning', onClick: (lote) => lote && handleInformation(lote), show: selectedDfd?.tramitacao === 'DEVOLVIDO PARA AJUSTES' },
              { key: 'excluir', label: 'Excluir', icon: 'bi-trash3', variant: 'danger', onClick: (lote) => lote && handleDelete(lote), requiresSelection: true, disabled: !dfdPrincipal || (dfdPrincipal.tramitacao !== 'EM ELABORAÇÃO' && dfdPrincipal.tramitacao !== 'DEVOLVIDO PARA AJUSTES') },
              { key: 'export', label: 'Exportar Excel', icon: 'bi-file-earmark-excel', variant: 'info', onClick: handleExportToExcel, disabled: progressState.show },
              { key: 'import', label: 'Importar Excel', icon: 'bi-file-earmark-arrow-down', variant: 'warning', onClick: () => setShowExcelUploadModal(true), show: dfdPrincipal?.tramitacao === 'EM ELABORAÇÃO' || dfdPrincipal?.tramitacao === 'DEVOLVIDO PARA AJUSTES', disabled: progressState.show },
              { key: 'itens', label: 'Itens', icon: 'bi-list-ul', variant: 'primary', onClick: (lote) => lote && navigate('/dfd_lote_itens', { state: { dfd: dfdPrincipal, dfdLote: lote } }), requiresSelection: true },
            ]}
          />

          <LoadingModal loading={isLoading} />

          <TableContainer tableClass="table-custom-radius">
            <TableHeader columns={[
              { label: 'Nº', width: '5%' },
              { label: 'Descrição' },
              { label: 'Qtd. Itens', width: '10%', align: 'center' },
            ]} />
            <tbody>
              {!isLoading && selectedDfds.length > 0 ? (
                selectedDfds.map(c => {
                  const isActiveRow = selectedDfd && selectedDfd.id === c.id;
                  return (
                    <tr
                      key={`${c.cnpj}-${c.dfdNum}-${c.id}`}
                      className={isActiveRow ? 'table-active' : ''}
                      style={{ cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                      onClick={() => handleRowClick(c)}
                      title={isActiveRow ? 'Clique novamente para desmarcar' : 'Clique para selecionar'}
                    >
                      <td style={{ width: '5%' }}>
                        <span className={isActiveRow ? 'fw-bold text-primary' : ''}>{c.id}</span>
                      </td>
                      <td>
                        <span className={isActiveRow ? 'fw-bold text-primary' : ''}>{c.descricao}</span>
                      </td>
                      <td className="text-center" style={{ width: '10%' }}>
                        <span className={isActiveRow ? 'fw-bold text-primary' : ''}>{c.dfdItemSet?.length || 0}</span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="3" className="text-center">
                    Nenhum registro encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </TableContainer>

          <PageFooter>
            <Pagination10 {...setPagination} />
          </PageFooter>

          <LoteModal
            isOpen={showLoteModal}
            dfd={dfdPrincipal}
            lote={loteParaEditar}
            onClose={() => { setShowLoteModal(false); setLoteParaEditar(null); }}
            onSuccess={handleSaveLote}
          />

          <ExcelUploadLoteModal
            show={showExcelUploadModal}
            onClose={() => setShowExcelUploadModal(false)}
            onSuccess={() => setShouldFetch(!shouldFetch)}
            dfd={dfdPrincipal}
          />

          <DefaultModal {...setDefaultModal} />
          <ConfirmModal {...setConfirmModal} />
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}
export default DfdLotes;
